import { useMemo } from 'react';
import { useAppStore } from '@indipia/shared';
import type { BadgeStatus } from '@indipia/ui-core';
import { useWorklogStore } from '../store/worklogStore';

/**
 * 공사진행상태(E01_SCHEDULE_STATUS) 라벨·색 해석 — work-log 공용.
 *
 * **라벨은 공통코드에서 가져온다.** 운영에서 코드명을 바꾸면 화면에 반영돼야 하므로 하드코딩하지 않는다.
 * 색만 코드값 기준 고정 매핑이다(디자인 결정이라 DB로 바뀌지 않는다).
 *
 * 조회 순서 — 앞에서 찾으면 뒤는 보지 않는다:
 *   ① worklogStore.codes — §9.1 `/common/codes` 직접 조회분. **정규 소스.**
 *      조회 대상 그룹은 worklogStore의 WORKLOG_CODE_GROUPS 에 등록한다.
 *      공사현황 진입마다 갱신되므로 코드명 변경이 재진입만으로 반영된다.
 *   ② appStore.codes — 플랫폼 init(`/indipia/init`) 적재분. 이 그룹은 sv-indipia 소관이라
 *      실제로는 담기지 않지만, 훗날 init에 포함되면 자동으로 동작하도록 남겨 둔다.
 *   ③ FALLBACK_LABEL — 조회 전/실패 시 최후 폴백. 그것도 없으면 코드값 그대로 노출
 *      (라벨을 비우면 화면에서 상태가 사라져 원인 파악이 어렵다).
 */
export const SCHEDULE_STATUS_GROUP = 'E01_SCHEDULE_STATUS';

/** 최후 폴백 — 공통코드 미적재 시에만 사용. SSOT는 어디까지나 §9.1 조회분이다. */
const FALLBACK_LABEL: Record<string, string> = {
  READY: '준비중',
  IN_PROGRESS: '시공중',
  DONE: '시공완료',
  DELAYED: '지연',
  CANCELED: '취소',
};

/** 코드값 → Badge status(D-09 §15.5 의미: 진행중=success · 완료=done · 안내=info · 경고=danger). 코드값 기준 고정(공통코드 대상 아님). 미등록 코드는 mute. */
export function scheduleStatusBadge(code: string): BadgeStatus {
  switch (code) {
    case 'READY': return 'info';
    case 'IN_PROGRESS': return 'success';
    case 'DONE': return 'done';
    case 'DELAYED': return 'danger';
    default: return 'mute';
  }
}

/**
 * 진행상태 코드 → 표시명 변환 함수를 돌려주는 훅.
 * 공통코드가 갱신되면 이 훅을 쓰는 화면이 함께 리렌더된다.
 */
export function useScheduleStatusName(): (code: string) => string {
  // ⚠️ 원시 배열을 select 하고 필터는 아래에서 한다 — 셀렉터에서 filter하면 매 렌더 새 배열이라 무한 렌더.
  const worklogCodes = useWorklogStore((s) => s.codes);
  const appCodes = useAppStore((s) => s.codes);
  return (code: string) =>
    worklogCodes.find((c) => c.codeGroupCode === SCHEDULE_STATUS_GROUP && c.codeValue === code)?.codeName
    ?? appCodes.find((c) => c.codeGroupCode === SCHEDULE_STATUS_GROUP && c.codeValue === code)?.codeName
    ?? FALLBACK_LABEL[code]
    ?? code;
}

/**
 * 진행상태 선택지 목록(필터 칩 등) — 공통코드 순서(codeOrder)를 따른다.
 * `exclude`로 특정 코드를 뺄 수 있다(예: 현장 목록 필터는 DELAYED 미노출).
 */
export function useScheduleStatusOptions(
  exclude: string[] = [],
): Array<{ code: string; codeName: string }> {
  const worklogCodes = useWorklogStore((s) => s.codes);
  const appCodes = useAppStore((s) => s.codes);

  return useMemo(() => {
    // ① §9.1 조회분 — 서버가 내려준 순서를 그대로 쓴다(BE가 codeOrder로 정렬해 준다).
    const fromStore = worklogCodes
      .filter((c) => c.codeGroupCode === SCHEDULE_STATUS_GROUP && !exclude.includes(c.codeValue))
      .map((c) => ({ code: c.codeValue, codeName: c.codeName }));
    if (fromStore.length > 0) return fromStore;

    // ② 플랫폼 init 적재분 — codeOrder 정렬
    const fromInit = appCodes
      .filter((c) => c.codeGroupCode === SCHEDULE_STATUS_GROUP && !exclude.includes(c.codeValue))
      .sort((a, b) => a.codeOrder - b.codeOrder)
      .map((c) => ({ code: c.codeValue, codeName: c.codeName }));
    if (fromInit.length > 0) return fromInit;

    // ③ 폴백(정의 순서 유지)
    return Object.entries(FALLBACK_LABEL)
      .filter(([code]) => !exclude.includes(code))
      .map(([code, codeName]) => ({ code, codeName }));
    // exclude는 호출부에서 리터럴 배열로 넘어와 매 렌더 새 참조라 join으로 안정화한다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [worklogCodes, appCodes, exclude.join(',')]);
}
